/**
 * Turn the support chat's reply text into render segments, linking ONLY
 * internal host console paths (#1056).
 *
 * The reply text comes from a language model, so it is untrusted input and
 * must never become an arbitrary-URL injection channel. The allowlist is
 * structural: a link segment is produced only for paths that start with
 * /host/ and contain nothing but [A-Za-z0-9/_-] — no scheme, no host, no
 * query string, no dots. Everything else in the reply stays plain text,
 * including external URLs and markdown links pointing anywhere else.
 */

export type ChatReplySegment =
    | { type: 'text'; text: string }
    | { type: 'link'; text: string; path: string };

// Two shapes the model produces: a markdown link whose target is an internal
// host path, and a bare host path. The lookbehind stops a path glued to the
// tail of an external URL ("https://evil.example/host/x") from linking.
const LINKABLE =
    /\[([^\]\n]{1,120})\]\((\/host\/[A-Za-z0-9/_-]{1,200})\)|(?<![\w.:/])(\/host\/[A-Za-z0-9/_-]{1,200})/g;

export const splitChatReply = (text: string): ChatReplySegment[] => {
    const segments: ChatReplySegment[] = [];
    let last = 0;
    for (const match of text.matchAll(LINKABLE)) {
        const index = match.index ?? 0;
        if (index > last) {
            segments.push({ type: 'text', text: text.slice(last, index) });
        }
        if (match[1] !== undefined && match[2] !== undefined) {
            segments.push({ type: 'link', text: match[1], path: match[2] });
        } else if (match[3] !== undefined) {
            segments.push({ type: 'link', text: match[3], path: match[3] });
        }
        last = index + match[0].length;
    }
    if (last < text.length) {
        segments.push({ type: 'text', text: text.slice(last) });
    }
    return segments;
};
